/*
 * Home · Community (sections/home-community.liquid).
 * - Video carousel mirroring Replo's carouselV3 as configured on the live page:
 *   infinite loop (slides cloned on both sides), one slide per arrow click,
 *   250ms ease slide, swipe/drag, horizontal mouse-wheel, no autoplay. The
 *   previous arrow is greyed out on the first slide but still wraps around.
 * - Videos load lazily when they scroll into view (Replo loading="lazy"),
 *   play with sound and loop; only the round button toggles play/pause.
 */
(function () {
  if (window.hmCommunityLoaded) return;
  window.hmCommunityLoaded = true;

  var DURATION = 250;

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- lazy video loading ---------- */
  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                loadVideo(entry.target);
                observer.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  function loadVideo(video) {
    var src = video.getAttribute('data-src');
    if (!src) return;
    video.removeAttribute('data-src');
    video.src = src;
  }

  function watchVideos(scope) {
    scope.querySelectorAll('video[data-src]').forEach(function (video) {
      if (observer) observer.observe(video);
      else loadVideo(video);
    });
  }

  /* ---------- video play buttons ---------- */
  function syncCard(video) {
    var card = video.closest('.hm-community__card');
    if (!card) return;
    var playing = !video.paused && !video.ended;
    card.classList.toggle('is-playing', playing);
    var button = card.querySelector('[data-hm-ugc-toggle]');
    if (button) button.setAttribute('aria-label', playing ? 'Pause' : 'Play');
  }

  function initVideos(root) {
    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-hm-ugc-toggle]');
      if (!button || !root.contains(button)) return;
      if (root.hmDragged) return;
      var video = button.closest('.hm-community__card').querySelector('video');
      if (!video) return;
      loadVideo(video);
      if (video.paused || video.ended) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        video.pause();
      }
    });

    ['play', 'pause', 'ended'].forEach(function (type) {
      root.addEventListener(
        type,
        function (event) {
          if (event.target.tagName === 'VIDEO') syncCard(event.target);
        },
        true
      );
    });
  }

  /* ---------- carousel ---------- */
  function initCarousel(root) {
    var carousel = root.querySelector('[data-hm-carousel]');
    if (!carousel) return;
    var viewport = carousel.querySelector('[data-hm-carousel-viewport]');
    var track = carousel.querySelector('[data-hm-carousel-track]');
    var prev = carousel.querySelector('[data-hm-carousel-prev]');
    var next = carousel.querySelector('[data-hm-carousel-next]');
    var status = carousel.querySelector('[data-hm-carousel-status]');
    var originals = Array.prototype.slice.call(track.querySelectorAll('[data-hm-slide]'));
    var count = originals.length;
    if (!count) return;

    function makeClone(slide) {
      var clone = slide.cloneNode(true);
      clone.removeAttribute('data-hm-slide');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.classList.add('is-clone');
      Array.prototype.slice.call(clone.attributes).forEach(function (attr) {
        if (attr.name.indexOf('data-shopify') === 0) clone.removeAttribute(attr.name);
      });
      clone.querySelectorAll('video').forEach(function (video) {
        if (video.getAttribute('src')) {
          video.setAttribute('data-src', video.getAttribute('src'));
          video.removeAttribute('src');
        }
      });
      clone.querySelectorAll('.is-playing').forEach(function (el) {
        el.classList.remove('is-playing');
      });
      return clone;
    }

    // Clone the full set before and after so the loop never shows a gap.
    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();
    originals.forEach(function (slide) {
      before.appendChild(makeClone(slide));
      after.appendChild(makeClone(slide));
    });
    track.insertBefore(before, track.firstChild);
    track.appendChild(after);

    var pos = count; // position in the full (cloned) list; count..2*count-1 = originals
    var index = 0;

    function step() {
      var slides = track.children;
      if (slides.length < 2) return 0;
      return slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left;
    }

    function apply(animate) {
      track.style.transition = animate && !reducedMotion() ? 'transform ' + DURATION + 'ms ease' : 'none';
      track.style.transform = 'translate3d(' + -pos * step() + 'px, 0, 0)';
    }

    function normalise() {
      if (pos >= 2 * count || pos < count) {
        pos = ((pos - count) % count + count) % count + count;
        apply(false);
        void track.offsetWidth;
      }
    }

    function update() {
      index = ((pos - count) % count + count) % count;
      if (prev) prev.classList.toggle('is-start', index === 0);
      if (status) status.textContent = 'Slide ' + (index + 1) + ' of ' + count;
    }

    function go(delta) {
      normalise();
      pos += delta;
      apply(true);
      update();
      if (reducedMotion()) normalise();
    }

    function goTo(i) {
      normalise();
      pos = count + i;
      apply(true);
      update();
    }

    track.addEventListener('transitionend', function (event) {
      if (event.target === track) normalise();
    });

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    // Horizontal mouse wheel / trackpad (Replo _mouseWheel).
    var wheelLock = 0;
    viewport.addEventListener(
      'wheel',
      function (event) {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 10) return;
        event.preventDefault();
        var now = Date.now();
        if (now < wheelLock) return;
        wheelLock = now + 400;
        go(event.deltaX > 0 ? 1 : -1);
      },
      { passive: false }
    );

    // Swipe / drag.
    var drag = null;
    viewport.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      normalise();
      drag = { x: event.clientX, y: event.clientY, dx: 0, moved: false, id: event.pointerId, base: -pos * step() };
      root.hmDragged = false;
    });

    viewport.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.id) return;
      drag.dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(drag.dx) < 6) return;
        if (Math.abs(event.clientY - drag.y) > Math.abs(drag.dx)) {
          drag = null;
          return;
        }
        drag.moved = true;
        root.hmDragged = true;
        try {
          viewport.setPointerCapture(event.pointerId);
        } catch (e) {}
        track.style.transition = 'none';
      }
      track.style.transform = 'translate3d(' + (drag.base + drag.dx) + 'px, 0, 0)';
    });

    function endDrag() {
      if (!drag) return;
      var d = drag;
      drag = null;
      if (!d.moved) return;
      var s = step() || 1;
      var slides = Math.abs(d.dx) > 40 ? Math.max(1, Math.round(Math.abs(d.dx) / s)) : 0;
      if (slides) go(d.dx < 0 ? slides : -slides);
      else apply(true);
      setTimeout(function () {
        root.hmDragged = false;
      }, 0);
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    // Keep the offset right when the slide width changes.
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        apply(false);
      }, 50);
    });

    // Theme editor: show the selected slide.
    root.addEventListener('shopify:block:select', function (event) {
      var i = originals.indexOf(event.target);
      if (i > -1) goTo(i);
    });

    apply(false);
    update();
  }

  /* ---------- buttons that scroll to an element on the page ---------- */
  // Replo "scroll to #reviews" action: smooth scroll, no offset. Falls back to
  // the normal anchor jump when the target id is missing.
  function initScrollLinks(root) {
    root.addEventListener('click', function (event) {
      var link = event.target.closest('[data-hm-scroll-link]');
      if (!link || !root.contains(link)) return;
      var id = decodeURIComponent((link.getAttribute('href') || '').replace(/^#/, ''));
      var target = id && document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    });
  }

  function init(root) {
    if (!root || root.hmCommunityInit) return;
    root.hmCommunityInit = true;
    initCarousel(root);
    initVideos(root);
    watchVideos(root);
    initScrollLinks(root);
  }

  function initAll(scope) {
    var el = scope || document;
    if (el.matches && el.matches('[data-hm-community]')) init(el);
    el.querySelectorAll('[data-hm-community]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
